import { FormError, Select } from "@msuite/ui/form";
import { useTranslation } from "@msuite/ui/localization";
import { useQuery } from "@msuite/ui/query";
import { responseHandler } from "@msuite/ui/utils";
import { guardArray } from "@msuite/utils/array";
import { guardString } from "@msuite/utils/strings";
import { FILE_FILING_COLLECTIONS, fileFilingNeedsRecord } from "@msuite/utils/files";
import type { FileFilingSchema } from "@msuite/utils/schemas";
import type { Nullable } from "@msuite/utils/types";
import { useFormContext } from "react-hook-form";
import { ProjectSearch } from "@/components/projects";
import { TicketSelectItem, type TicketSelectItemProps } from "@/components/tickets";
import { client } from "@/hono";

/**
 * One record the picker can file onto. Everything but a ticket renders as its `label` alone, so
 * `ticket` is null for every other collection — it carries what the ticket option draws instead.
 */
interface FilingRecord {
	id: string;
	label: string;
	ticket: Nullable<TicketSelectItemProps>;
}

/**
 * Where an inbox file is being filed: the collection, the project, and the record within it.
 *
 * The three fields are one chain and read that way — a project narrows the records, and a
 * collection decides whether a record is asked for at all. A project document needs none, which is
 * why it is the first option: it is the destination that always works, for the document nobody can
 * pin to a ticket or a protocol.
 *
 * Headless, like every form here: the modal around it owns the submit.
 */
export function FileFilingForm() {
	/** Context */
	const methods = useFormContext<FileFilingSchema>();

	/** Hooks */
	const { t } = useTranslation();

	/** State */
	const collection = methods.watch("collection");
	const projectId = methods.watch("projectId");

	/** Query */
	const {
		data: records,
		isLoading,
		isError,
	} = useQuery({
		queryKey: ["file-inbox-records", collection, projectId ?? "--"],
		queryFn: () => fetchRecords(collection, projectId),
		enabled: !!projectId && fileFilingNeedsRecord(collection),
	});

	/** Functions */
	async function fetchRecords(
		target: FileFilingSchema["collection"],
		project: string,
	): Promise<FilingRecord[]> {
		if (target === "ticketFiles") {
			const response = await client.projects[":projectId"].tickets.$get({
				param: { projectId: project },
				query: { state: ["active"] },
			});
			const data = await responseHandler(response, false);
			return guardArray(data.tickets).map((ticket) => ({
				id: ticket.id,
				label: `#${ticket.number} ${guardString(ticket.description)}`,
				ticket: {
					number: ticket.number,
					description: ticket.description,
					icon: ticket._type?.icon,
					color: ticket._type?.color,
				},
			}));
		}
		const response = await client.protocols.index.$get({ query: { projectId: project } });
		const data = await responseHandler(response, false);
		return guardArray(data.protocols).map((protocol) => ({
			id: protocol.id,
			label: guardString(protocol.title),
			ticket: null,
		}));
	}

	/**
	 * What the record picker says beneath itself while it is disabled. It is disabled for four
	 * different reasons and the difference is the whole point: before a project is chosen there is
	 * nothing to list yet, while the list is still loading there is nothing to report, a failed
	 * lookup has to say so here because it raises no toast of its own, and an empty answer is a
	 * fact about the project that the person can only act on once told it.
	 */
	function recordHelperText() {
		if (!projectId) return t("files.inbox_pick_project_first");
		if (isLoading) return undefined;
		if (isError) return t("files.inbox_records_failed");
		if (guardArray(records).length) return undefined;
		if (collection === "ticketFiles") return t("files.inbox_no_active_tickets");
		return t("files.inbox_no_protocols");
	}

	/** Render */
	return (
		<>
			<Select
				isRequired
				isNullable={false}
				label={t("files.inbox_file_it_into")}
				control={methods.control}
				name="collection"
				// Choosing a different collection invalidates whatever record was picked under the
				// last one: a ticket id in the protocol field would be refused by the parent proof,
				// and the refusal would read as the file being unfilable rather than as a stale field.
				onChange={() => methods.setValue("recordId", null)}
				options={FILE_FILING_COLLECTIONS.map((value) => ({
					value,
					label: t(`files.inbox_collections.${value}`),
					item: { value },
				}))}
			/>
			<div className="v-stack">
				<ProjectSearch
					label="projects.project"
					isNullable={false}
					defaultProjectId={methods.getValues("projectId")}
					onChange={(project) => {
						if (!project) return;
						methods.setValue("projectId", project);
						methods.setValue("recordId", null);
					}}
				/>
				{/* The picker carries its own form, so the parent's error on this field has no input to
				    hang under and is rendered here instead. */}
				<FormError
					errors={methods.formState.errors}
					name="projectId"
				/>
			</div>
			{fileFilingNeedsRecord(collection) && (
				<Select
					isRequired
					label={t("files.inbox_record")}
					control={methods.control}
					name="recordId"
					isDisabled={!guardArray(records).length}
					options={guardArray(records).map((record) => ({
						value: record.id,
						label: record.label,
						item: record,
					}))}
					// Only a ticket has more to show than its label — its number reads as the heading of
					// its own description, and the icon is the type, which is how a ticket is recognised
					// everywhere else in the app. The other two collections keep the plain label.
					renderOption={(record) =>
						record.ticket ? (
							<TicketSelectItem {...record.ticket} />
						) : (
							<p className="truncate">{record.label}</p>
						)
					}
					helperText={recordHelperText()}
				/>
			)}
		</>
	);
}
